import usePrivateAxios from "@/common/api/usePrivateAxios";
import { removeSpecialCharacters } from "@/common/utils/unFormatters";
import { transformNewRoles } from "@/common/utils/roleTransformers";
import { dateBrStringToISO } from "@/common/utils/dateTransformer";
import { CompanyUserValidationSchema } from "@/features/privateCompanies/schemas/create.schema";
import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

interface UseCreateCompanyUser {
  companyId?: string,
  file: Blob | undefined
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError) => void
}

const useCreateCompanyUser = ({ companyId, file, onSuccess, onError }: UseCreateCompanyUser) => {
  const privateAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['createCompanyUser'],
    mutationFn: async (form: CompanyUserValidationSchema) => {
      const driverLicenseNumber = form.current_driver_license_registration_number
        ? removeSpecialCharacters(form.current_driver_license_registration_number)
        : null;

      const driverLicenseExpirationDate = dateBrStringToISO(
        form.current_driver_license_expiration_date,
      );

      const dateOfBirth = dateBrStringToISO(form.date_of_birth);

      const payload = {
        company_id: companyId,
        send_passwords_on_creation: form.send_passwords_on_creation,
        cpf: removeSpecialCharacters(form.cpf),
        date_of_birth: dateOfBirth,
        full_name: form.full_name,
        status: form?.status?.value || 'enabled',
        branch_id: form?.branch_id?.value || null,
        phone_attributes: form?.phone?.number
          ? { number: form?.phone?.number ? removeSpecialCharacters(form?.phone?.number) : null }
          : undefined,
        email: form.email || null,
        enrollment_number: form.enrollment_number || null,
        user_roles_attributes: form?.roles?.length ? form.roles.map(transformNewRoles) : [],
        current_driver_license_registration_number: driverLicenseNumber,
        current_driver_license_expiration_date: driverLicenseExpirationDate,
        user_driver_license_categories_attributes: form.user_driver_license_categories?.length
          ? form.user_driver_license_categories.map((category) => ({
              license_category_id: category.value,
            }))
          : [],
        name_on_card: form.name_on_card || null,
        profile_picture: file ? file : undefined
      }

      return privateAxios.postForm('/client/users', { client_user: payload })
    },
    onSuccess,
    onError,
  });
}

export default useCreateCompanyUser
